import {MUTATION_KEYS} from '@/queryClient.ts'
import {useMutation} from '@tanstack/react-query'
import {AxiosError} from 'axios'
import {ErrorResponseData} from '@/types'
import {parseAxiosPromise} from '@/axiosInstance.ts'
import {captureException} from '@sentry/react'
import {HttpImpersonatePayload, HttpImpersonateResponse, httpImpersonate} from './auth.http'
import {useAuthStore} from '@/features/auth/store/store'
import {toast} from '@/utilities/toast/toast.tsx'
import i18next from 'i18next'

type UseImpersonateOptions = {
    onError?: (error: AxiosError<ErrorResponseData>) => void
}

/**
 * Swaps the operator's session for the target user's one, then reloads: App.tsx re-derives the whole
 * session from localStorage on boot, so a reload is what guarantees no cached data from the operator's
 * identity leaks into the impersonated one.
 */
export const useImpersonate = (options?: UseImpersonateOptions) =>
    useMutation({
        mutationKey: [MUTATION_KEYS.IMPERSONATE],
        mutationFn: (payload: HttpImpersonatePayload) =>
            parseAxiosPromise({
                axiosPromise: httpImpersonate(payload),
                onZodError: captureException,
                responseShape: HttpImpersonateResponse
            }),
        onSuccess: data => {
            useAuthStore.getState().startImpersonation({accessToken: data.accessToken, user: data.user})
            window.location.reload()
        },
        onError: (error: AxiosError<ErrorResponseData>) => {
            // The session is left untouched: nothing has been written to storage before this point.
            toast.error(i18next.t('impersonation:error_start'))
            options?.onError?.(error)
        }
    })
